import React, { createContext, useContext } from "react";
import { gql, useQuery } from "@apollo/client";
import { GET_ROLE, RoleResult } from "../Queries";

interface User {
  id: string;
  firstName: string;
  lastName: string;
}

interface UserContextType {
  currentUser?: User;
  loading: boolean;
  isStudent: boolean;
  isInstructor: boolean;
  isInstanceAdmin: boolean;
  hasCourseContext: boolean;
}

const UserContext = createContext<UserContextType>({
  loading: true,
  isStudent: false,
  isInstructor: false,
  isInstanceAdmin: false,
  hasCourseContext: false,
});

const GET_CURRENT_USER = gql`
  query GetCurrentUser {
    me {
      id
      firstName
      lastName
    }
  }
`;

interface GetCurrentUserResponse {
  me: User;
}

export const UserProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { data, loading } = useQuery<GetCurrentUserResponse>(GET_CURRENT_USER);
  const { data: roleData } = useQuery<RoleResult>(GET_ROLE);

  const {
    myRole,
    isInstanceAdmin = false,
    hasCourseContext = false,
  } = roleData || {};

  return (
    <UserContext.Provider
      value={{
        currentUser: data?.me,
        loading,
        isInstanceAdmin,
        hasCourseContext,
        isStudent: myRole === "student",
        isInstructor: myRole === "instructor",
      }}
    >
      {children}
    </UserContext.Provider>
  );
};

export const useUser = () => {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error("useUser must be used within a UserProvider");
  }
  return context;
};
